import CustomTag from "components/Atoms/CustomTag"
import CustomTooltip from "components/Atoms/CustomTooltip"
import StatusIcon from "components/Atoms/StatusIcon"
import StyledLink from "components/Atoms/StyledLink"
import RowSelector, {
  RowSelectorAll,
  RowSelectorColumn,
} from "components/Molecules/RowSelector"
import CustomReactTable from "components/Templates/CustomReactTable"
import PopoverTooltip from "components/Templates/PopoverTooltip"
import TimeCell from "components/Templates/table/TimeCell"
import { useOrderList } from "graphql/order/order.hooks"
import { pick, set } from "lodash-es"
import React from "react"
import { useSearchParams } from "react-router-dom"
import clsx from "utils/clsx"
import { arrayToMap } from "utils/custom.utils"
import { inr } from "utils/formatters.utils"
import { ORDER_STATUSES } from "../../data/orderStatuses"
import { ORDER_TYPES_MAP } from "../../data/orderTypes"
import { PAYMENT_METHODS, PAYMENT_STATUSES } from "../../data/static.items"
import OrderActions from "./table/OrderActions"
const orderStatusMap = arrayToMap(ORDER_STATUSES, st => {
  const obj = { intent: "primary", text: st?.name }
  if (/(cancel)|(reject)/gi.test(st?.name)) {
    set(obj, "intent", "danger")
    return obj
  }
  if (st?.id === "CMP") {
    set(obj, "intent", "success")
    return obj
  }

  if (st?.id === "CNF") {
    set(obj, "intent", "warning")
    return obj
  }

  return obj
})

const paymentStatusMap = arrayToMap(PAYMENT_STATUSES)

const paymentMethodMap = arrayToMap(PAYMENT_METHODS)

const COLUMNS = [
  {
    ...RowSelectorColumn,
    accessor: "id",
    Header({ data }) {
      return (
        <RowSelectorAll
          path="orders"
          payload={data?.map(({ _original }) =>
            pick(_original, [
              "id",
              "user",
              "trackingCode",
              "orderStatus",
              "isConsolidated",
            ])
          )}
        />
      )
    },
    Cell({ original }) {
      return (
        <RowSelector
          path="orders"
          payload={pick(original, [
            "id",
            "user",
            "trackingCode",
            "orderStatus",
            "isConsolidated",
          ])}
        />
      )
    },
  },
  {
    width: 45,
    Cell({ original }) {
      return <OrderActions order={original} />
    },
  },

  {
    Header: "Status",
    accessor: "orderStatus",
    Cell({ value }) {
      return (
        <div className="vt-text-left">
          <StatusIcon
            className={value === "IPR" ? "vt-order-ipr" : undefined}
            values={orderStatusMap}
            condition={value}
          />
        </div>
      )
    },
  },
  {
    minWidth: 145,
    Header: "Order",
    accessor: "trackingCode",
    Cell({ value, original }) {
      const [params, setParams] = useSearchParams()
      return (
        <>
          <div className="od-tracking-code">
            <div>
              <span
                className="vt-styled-link-text"
                onClick={() => {
                  params.set("id", original?.id)
                  setParams(params)
                }}
              >
                {value}
              </span>
            </div>
            <div>
              {original?.isConsolidated && (
                <CustomTag className="ml-2" round intent="success" minimal>
                  Merged
                </CustomTag>
              )}
            </div>
          </div>
          <div className="od-tracking-code">
            <span className="vt-muted text-xs">
              {ORDER_TYPES_MAP[original?.orderCategory]?.name}
            </span>
          </div>
        </>
      )
    },
  },
  {
    Header: "SO Number",
    accessor: "soNumber",
  },
  {
    Header: "Vendor",
    accessor: "user",
    Cell({ value }) {
      return (
        <>
          <StyledLink to={`/d/registration/vendors/details/${value?.id}`}>
            {value?.fullName}
          </StyledLink>
          <div className="vt-muted text-xs">{value?.number}</div>
        </>
      )
    },
  },
  {
    Header: "Placed At",
    accessor: "createdAt",
    Cell({ value }) {
      return <TimeCell timeStamp={value} />
    },
  },
  {
    Header: "Shipping Address",
    accessor: "shippingAddress",
    Cell({ value }) {
      const formatted = value?.district?.split(",")

      return (
        <div className="vt-text-left">
          <PopoverTooltip
            trigger={
              <div style={{ cursor: "pointer" }}>
                <div className="vt-muted">{value?.village}</div>
              </div>
            }
          >
            <div className="p-2 text-xs">
              <div className="text-sm vt-black mb-1">Address Details</div>
              {formatted?.map((al, idx) => {
                return (
                  <p key={idx} className="mb-0">
                    {al},
                  </p>
                )
              })}
            </div>
          </PopoverTooltip>
        </div>
      )
    },
  },
  {
    Header: "Order Amount",
    accessor: "totalAmount",
    Cell({ value, original }) {
      const method = original?.paymentMethod
      return (
        <div className="vt-text-left px-3">
          <CustomTooltip
            content={paymentStatusMap?.[original?.paymentStatus]?.name}
          >
            <div
              className={clsx(
                "cursor-pointer",
                original.paymentStatus === "SUC" && "vt-success",
                original.paymentStatus === "FAL" && "vt-danger",
                original.paymentStatus === "RUF" && "vt-warning"
              )}
            >
              {inr(value)}
            </div>
          </CustomTooltip>

          <div className="vt-muted">{paymentMethodMap?.[method]?.name}</div>
        </div>
      )
    },
  },
  {
    Header: "Payment",
    accessor: "totalPaid",
    Cell({ original, value }) {
      const status = PAYMENT_STATUSES.find(
        t => t.id === original?.paymentStatus
      )
      return (
        <div className="vt-text-left px-3">
          <div>{inr(value)}</div>

          <div
            className={clsx("vt-muted", status?.id === "CAP" && "vt-success")}
          >
            {status?.name}
          </div>
        </div>
      )
    },
  },
  {
    sortable: false,
    Header: "Total Outstanding",
    accessor: "totalPaid",
    Cell({ original, value }) {
      const total = original?.totalAmount - value

      return (
        <div className="vt-text-left px-3">
          <div>{inr(total)}</div>
        </div>
      )
    },
  },
  {
    Header: "Estd. Delivery",
    accessor: "orderItems.0.shipping.estimatedDelivery",
    Cell({ value }) {
      return <TimeCell timeStamp={value} />
    },
  },
  {
    Header: "Pickup Date",
    accessor: "pickupDate",
    Cell({ original }) {
      const value = original?.pickupDate
      return value ? <TimeCell timeStamp={value} /> : "--"
    },
  },
]
const OrderTable = () => {
  const { data, loading } = useOrderList()
  const orders = data?.allOrders ?? []
  return (
    <CustomReactTable
      getTrProps={(_, row) => {
        const status = row?.original?.orderStatus
        if (status === "CMP") {
          return {
            className: "vt-order-completed",
          }
        } else if (
          status === "CBV" ||
          status === "CBU" ||
          status === "PRJ" ||
          status === "FRJ"
        ) {
          return {
            className: "vt-order-cancelled",
          }
        } else if (status === "CNF") {
          return {
            className: "vt-order-confirmed",
          }
        } else {
          return {
            className: "vt-order-processing",
          }
        }
      }}
      pageSize={orders?.length > 15 ? orders?.length : 15}
      loading={loading}
      data={orders}
      columns={COLUMNS}
    />
  )
}

export default OrderTable
